import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyIcon from '@mui/icons-material/Key';
import ScienceIcon from '@mui/icons-material/Science';
import {
  deleteConnectorCredential,
  listConnectorCredentials,
  listConnectors,
  saveConnectorCredential,
  testConnector,
  testConnectorCredential,
} from '../../api/connectors.api';

function parseFlow(text) {
  try {
    const def = JSON.parse(text || '{}');
    return def && typeof def === 'object' ? def : null;
  } catch {
    return null;
  }
}

function sampleParamsFromSchema(schema) {
  const out = {};
  Object.entries(schema?.properties || {}).forEach(([key, prop]) => {
    if (prop.default != null) out[key] = prop.default;
    else if (prop.enum?.length) out[key] = prop.enum[0];
    else if (prop.type === 'number' || prop.type === 'integer') out[key] = 0;
    else if (prop.type === 'object') out[key] = {};
    else if (prop.type === 'array') out[key] = [];
    else out[key] = '';
  });
  return out;
}

export default function StudioV2IntegrationCatalog({ text, setText }) {
  const [connectors, setConnectors] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [error, setError] = useState('');
  const [testBusy, setTestBusy] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [credentialRefByConnector, setCredentialRefByConnector] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [c, refs] = await Promise.all([listConnectors(), listConnectorCredentials()]);
      if (cancelled) return;
      setConnectors(c.connectors);
      setCredentials(refs.credentials);
      setError(c.error || refs.error || '');
    })();
    return () => { cancelled = true; };
  }, []);

  const reloadCredentials = async () => {
    const refs = await listConnectorCredentials();
    setCredentials(refs.credentials);
    if (refs.error) setError(refs.error);
  };

  const saveCredential = async (connector, draft) => {
    const { error: err } = await saveConnectorCredential(draft.ref, {
      connectorId: connector.connectorId,
      authType: draft.authType,
      label: draft.label,
      secret: draft.secret,
      header: draft.authType === 'api_key' ? draft.header?.trim() : undefined,
    });
    if (err) {
      setError(err);
      return false;
    }
    await reloadCredentials();
    setCredentialRefByConnector((prev) => ({ ...prev, [connector.connectorId]: draft.ref }));
    return true;
  };

  const removeCredential = async (ref) => {
    const { error: err } = await deleteConnectorCredential(ref);
    if (err) setError(err);
    await reloadCredentials();
  };

  const credentialsByConnector = useMemo(() => {
    const by = {};
    credentials.forEach((cred) => {
      const key = cred.connectorId || 'general';
      by[key] = [...(by[key] || []), cred];
    });
    return by;
  }, [credentials]);

  const addConnectorNode = (connector, operation) => {
    const def = parseFlow(text);
    if (!def) {
      setError('Current flow JSON is invalid.');
      return;
    }
    const nodes = Array.isArray(def.nodes) ? def.nodes : [];
    const idBase = `${connector.connectorId}_${operation.operationId}`.replace(/[^a-zA-Z0-9_]/g, '_');
    const id = `${idBase}_${Date.now()}`;
    const node = {
      id,
      type: 'connector',
      label: `${connector.name}: ${operation.name}`,
      position: { x: 160 + nodes.length * 40, y: 160 + nodes.length * 30 },
      config: {
        connectorId: connector.connectorId,
        operationId: operation.operationId,
        credentialRef: credentialRefByConnector[connector.connectorId] || '',
        inputMapping: sampleParamsFromSchema(operation.inputSchema),
        outputVariable: `${connector.connectorId}_${operation.operationId}_result`,
      },
    };
    setText(JSON.stringify({ ...def, nodes: [...nodes, node] }, null, 2));
  };

  const runTest = async (connector, operation) => {
    const key = `${connector.connectorId}:${operation.operationId}`;
    setTestBusy(key);
    setTestResult(null);
    const { result, error: err } = await testConnector({
      connectorId: connector.connectorId,
      operationId: operation.operationId,
      credentialRef: credentialRefByConnector[connector.connectorId] || '',
      params: sampleParamsFromSchema(operation.inputSchema),
      dryRun: true,
    });
    setTestBusy('');
    setTestResult({
      connector: connector.connectorId, operation: operation.operationId, result, error: err,
    });
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>Integration catalog</Typography>
        <Typography variant="body2" color="text.secondary">
          Add connector nodes from the backend registry. Flow JSON stores credential reference names only; secrets are encrypted in this workspace&apos;s credential store.
        </Typography>
      </Box>
      {error && <Alert severity="warning">{error}</Alert>}
      {testResult && (
        <Alert severity={testResult.error || testResult.result?.success === false ? 'warning' : 'success'}>
          <Typography variant="body2" fontWeight={700}>
            {testResult.connector}
            {' / '}
            {testResult.operation}
          </Typography>
          <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
            {JSON.stringify(testResult.error || testResult.result, null, 2)}
          </Typography>
        </Alert>
      )}
      {!connectors.length && !error && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Loading connectors...</Typography>
        </Box>
      )}
      <Stack spacing={1.5}>
        {connectors.map((connector) => (
          <Card key={connector.connectorId} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle2" fontWeight={700}>{connector.name}</Typography>
                  <Chip size="small" label={connector.connectorId} />
                  {(connector.authTypes || []).map((auth) => <Chip key={auth} size="small" variant="outlined" label={auth} />)}
                </Stack>
                <Typography variant="body2" color="text.secondary">{connector.description}</Typography>
                <TextField
                  select
                  size="small"
                  label="Credential ref"
                  value={credentialRefByConnector[connector.connectorId] || ''}
                  onChange={(e) => setCredentialRefByConnector((prev) => ({ ...prev, [connector.connectorId]: e.target.value }))}
                  helperText="Reference name only. No secret is saved into the flow."
                  sx={{ maxWidth: 360 }}
                >
                  <MenuItem value="">
                    <em>None / provide at runtime</em>
                  </MenuItem>
                  {(credentialsByConnector[connector.connectorId] || []).map((cred) => (
                    <MenuItem key={cred.id} value={cred.id}>{cred.name || cred.id}</MenuItem>
                  ))}
                </TextField>
                <CredentialManager
                  connector={connector}
                  credentials={credentialsByConnector[connector.connectorId] || []}
                  onSave={(draft) => saveCredential(connector, draft)}
                  onDelete={removeCredential}
                  onTested={reloadCredentials}
                />
                <Stack spacing={1}>
                  {(connector.operations || []).map((operation) => {
                    const busy = testBusy === `${connector.connectorId}:${operation.operationId}`;
                    return (
                      <PaperLikeOperation
                        key={operation.operationId}
                        connector={connector}
                        operation={operation}
                        busy={busy}
                        onTest={() => runTest(connector, operation)}
                        onAdd={() => addConnectorNode(connector, operation)}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}

function lastTestText(lastTest) {
  if (!lastTest?.at) return 'Not tested yet';
  const when = new Date(lastTest.at).toLocaleString();
  return `${lastTest.ok ? 'Worked' : 'Failed'} ${when}${lastTest.message ? `: ${lastTest.message}` : ''}`;
}

/** Test one stored credential with the connector's read-only check (HTTP needs a URL to call). */
function CredentialTester({ credential, needsUrl, onTested }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = async () => {
    setBusy(true);
    setError('');
    const { error: err } = await testConnectorCredential(credential.id, { url: needsUrl ? url.trim() : undefined });
    setBusy(false);
    if (err) setError(err);
    await onTested?.();
  };

  const last = credential.lastTest;
  return (
    <Stack spacing={0.5} data-testid={`credential-test-${credential.id}`}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>{credential.id}</Typography>
        {needsUrl && (
          <TextField
            size="small"
            label="URL to test against"
            placeholder="https://api.example.com/me"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            sx={{ flex: 1 }}
          />
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={busy ? <CircularProgress size={14} /> : <ScienceIcon />}
          disabled={busy || (needsUrl && !/^https?:\/\/\S+$/.test(url.trim()))}
          onClick={run}
        >
          Test connection
        </Button>
      </Stack>
      <Typography
        variant="caption"
        color={last?.at ? (last.ok ? 'success.main' : 'error.main') : 'text.secondary'}
        data-testid={`credential-last-test-${credential.id}`}
      >
        {lastTestText(last)}
        {credential.lastUsedAt ? ` · Last used by a flow ${new Date(credential.lastUsedAt).toLocaleString()}` : ''}
      </Typography>
      {error && <Alert severity="warning" sx={{ py: 0 }}>{error}</Alert>}
    </Stack>
  );
}

function CredentialManager({
  connector, credentials, onSave, onDelete, onTested,
}) {
  const authTypes = connector.authTypes?.length ? connector.authTypes : ['bearer'];
  const blank = {
    ref: `${connector.connectorId}_default`, label: '', authType: authTypes[0], secret: '', header: '',
  };
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const ok = await onSave(draft);
    setBusy(false);
    if (ok) setDraft(null);
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
        {credentials.map((cred) => (
          <Chip
            key={cred.id}
            size="small"
            icon={<KeyIcon />}
            label={cred.id}
            onDelete={() => onDelete(cred.id)}
            data-testid={`credential-chip-${cred.id}`}
          />
        ))}
        {!draft && (
          <Button size="small" startIcon={<KeyIcon />} onClick={() => setDraft(blank)}>
            Add credential
          </Button>
        )}
      </Stack>
      {credentials.map((cred) => (
        <CredentialTester
          key={`test-${cred.id}`}
          credential={cred}
          needsUrl={connector.connectorId === 'http'}
          onTested={onTested}
        />
      ))}
      {draft && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            label="Reference name"
            value={draft.ref}
            onChange={(e) => setDraft({ ...draft, ref: e.target.value.replace(/[^a-zA-Z0-9_.-]/g, '_') })}
          />
          <TextField
            select
            size="small"
            label="Auth"
            value={draft.authType}
            onChange={(e) => setDraft({ ...draft, authType: e.target.value })}
            sx={{ minWidth: 110 }}
          >
            {authTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          {draft.authType === 'api_key' && (
            <TextField
              size="small"
              label="Header"
              placeholder="X-API-Key"
              value={draft.header}
              onChange={(e) => setDraft({ ...draft, header: e.target.value.replace(/[^A-Za-z0-9-]/g, '') })}
              sx={{ minWidth: 130 }}
            />
          )}
          <TextField
            size="small"
            type="password"
            label="Secret"
            autoComplete="new-password"
            value={draft.secret}
            onChange={(e) => setDraft({ ...draft, secret: e.target.value })}
            sx={{ flex: 1 }}
          />
          <Button size="small" variant="contained" disabled={busy || !draft.ref || !draft.secret} onClick={submit}>
            Save
          </Button>
          <Button size="small" onClick={() => setDraft(null)}>Cancel</Button>
        </Stack>
      )}
    </Stack>
  );
}

function PaperLikeOperation({
  operation, busy, onTest, onAdd,
}) {
  return (
    <Box sx={{
      p: 1, border: 1, borderColor: 'divider', borderRadius: 1.5,
    }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700}>{operation.name}</Typography>
          <Typography variant="caption" color="text.secondary">{operation.description}</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <Chip size="small" variant="outlined" label={operation.operationId} />
            {operation.category && <Chip size="small" variant="outlined" label={operation.category} />}
            {(operation.inputSchema?.required || []).map((key) => <Chip key={key} size="small" color="warning" variant="outlined" label={`required: ${key}`} />)}
          </Stack>
        </Box>
        <Button size="small" variant="outlined" startIcon={busy ? <CircularProgress size={14} /> : <ScienceIcon />} onClick={onTest} disabled={busy}>
          Test
        </Button>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={onAdd}>
          Add node
        </Button>
      </Stack>
    </Box>
  );
}
